<!--礼物栏-->
<template>
  <div class="gift-control">
    <div class="gift-list">
      <gift-item
        v-for="item in giftListTop"
        :key="item.id"
        :gift-item-data="item"
        @openDialogBalance="openDialogBalance"
        @closeDialogBalance="closeDialogBalance"
        @openIframeView = "openIframeView"
      />
    </div>
    <div class="gift-expand" @click.stop="toggle">
      <div class="expand-icon" :class="{expanded: expanded}"></div>
    </div>
    <div class="stars">
      <ElTooltip
        placement="top"
        :content="$t('gift.starDesc')"
        effect="light"
        :teleported="false"
        :offset="8"
        popper-class="tooltip-star-desc"
      >
        <div class="title">Stars</div>
      </ElTooltip>

      <template v-if="isLogin">
        <ElTooltip placement="top" :show-arrow="false" :content="starsNum" effect="light" :teleported="false" :offset="4">
          <div class="balance">{{starsFormatted}}</div>
        </ElTooltip>
      </template>
      <template v-else>
        <div class="balance">{{starsFormatted}}</div>
      </template>

      <div class="top-up-btn" @click="topup">{{$t('gift.topUp')}}</div>
    </div>
  </div>
  <div class="gift-panel-wrapper" :class="{'not-expanded':!expanded}" @click.prevent.stop>
    <gift-panel
      v-if="expanded||lazyExpanded"
      :list="giftList"
      :panel-row-count="panelRowCount"
      :height="316"
      @openDialogBalance="openDialogBalance"
      @closeDialogBalance="closeDialogBalance"
      @openIframeView = "openIframeView"
      v-expose="allGiftDashboardShowMeta"
    />
  </div>
  <ElDialog v-model="dialogBalanceVisible" custom-class="dialog-top_up" top="28vh" :append-to-body="true">
    <template #header>
      <div class="dialog-top_up__header">{{$t('balance.insufficientTitle')}}</div>
    </template>
    <div class="dialog-top_up__body">
      <div class="content">{{$t('balance.insufficientContent')}}</div>
      <div class="btns">
        <div class="cancel-btn" @click="dialogBalanceVisible=false">{{$t('common.cancel')}}</div>
        <div class="buy-btn" @click="buy">{{$t('balance.buy')}}</div>
      </div>
    </div>
  </ElDialog>
  <IframeView
      :url="iframeViewUrl"
      :width="375" 
      :height="460" 
      :draggable="true" 
      :extensible="false"
      v-model:show="iframeViewShow"
    ></IframeView>
</template>


<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted,watch } from 'vue';
import { useStore } from 'vuex';
import { ElDialog, ElTooltip } from 'element-plus';
import { useLogin } from '@bilibili-intl/login';
import { useGiftCache } from '@/common/gift-cache';
import { usePayment } from '../payment';
import { roomTracker } from '@/track/room';
import { useServerPrefetch } from '@/composables/use-server-prefetch';
import { IGiftItem } from './gift-types';
import GiftPanel from './gift-panel.vue';
import GiftItem from './gift-item.vue';
import IframeView from '@/components/iframe-view.vue';

const store = useStore();

useServerPrefetch(async() => await useGiftCache(), store.state.room.giftConfig);
useServerPrefetch(async() => await store.dispatch('getGiftPanel'), store.state.room.giftPanel);
useServerPrefetch(async() => await store.dispatch('getBalance'), store.state.room.balance);


const isLogin = computed(() => store.state.global.userInfo?.is_login);
const roomId = computed(() => store.state.room.roomInfo.room_id);
const loginContext = useLogin();
const payment = usePayment();
const allGiftDashboardShowMeta = roomTracker.getAllGiftDashboardShowMeta(roomId.value);
const expanded = ref(false);
const lazyExpanded = ref(false);


const topCount = ref(6);
const panelRowCount = ref(4);
const giftList = computed(() => {
  const giftPanel = store.state.room.giftPanel || [];
  const giftConfig = store.state.room.giftConfig || {};

  return giftPanel.map(
    (item: IGiftItem) => {
      const gift = giftConfig[item.id];
      item = {
        ...item,
        ...gift,
      };
      return item;
    }
  );
});
const giftListTop = computed(() => giftList.value.slice(0, topCount.value));

watch(
  () => expanded.value,
  (value, preValue) => {
    setTimeout(() => {
      lazyExpanded.value = value;
    }, 200);
  }
);

const toggle = () => {
  if(!expanded.value){
    roomTracker.GiftDashboardExpandClick(roomId.value);
  }
  expanded.value = !expanded.value;
};

const starsFormatted = computed(() => {
  const stars = store.state.room.balance;
  if (stars === null) {
    return '--';
  }
  if (stars >= 1e7) {
    return (stars / 1e6).toFixed(1) + 'M';
  }
  if (stars > 1e5) {
    return (stars / 1e3).toFixed(1) + 'K';
  }
  return stars;
});

const starsNum = computed(() => {
  const stars = store.state.room.balance;
  if (stars === null) {
    return '';
  }
  return stars.toString().replace(/\d+/, (n: string) => {
    return n.replace(/(\d)(?=(?:\d{3})+$)/g, '$1,');
  });
});

const handleResize = () => {
  if (window.innerWidth < 1080) {
    topCount.value = 4;
    panelRowCount.value = 3;
  } else {
    topCount.value = 6;
    panelRowCount.value = 4;
  }
};

const hidePopPanel = () => {
  if (expanded.value) {
    expanded.value = false;
  }
};

const topup = () => {
  roomTracker.RechargeClick(roomId.value);

  if (!isLogin.value) {
    return loginContext.openLogin();
  }
  payment.open();
};

const dialogBalanceVisible = ref(false);
const dialogBalanceGift = ref();
const openDialogBalance = (giftItemData: IGiftItem) => {
  dialogBalanceVisible.value = true;
  dialogBalanceGift.value = giftItemData;
};
const closeDialogBalance = () => {
  dialogBalanceVisible.value = false;
};
const buy = () => {
  const difference = Math.abs(store.state.room?.balance - dialogBalanceGift.value.stars);

  payment.open(difference);
  dialogBalanceVisible.value = false;
};

const iframeViewUrl = ref('');
const iframeViewShow = ref(false);
const openIframeView = (url:string) => {
  iframeViewUrl.value = url;
  iframeViewShow.value = true;
};

onMounted(async () => {
  window.addEventListener('resize', handleResize);
  document.body.addEventListener('click', hidePopPanel);
  handleResize();
  payment.onSuccessPayed(() => store.dispatch('getBalance'));
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  document.body.removeEventListener('click', hidePopPanel);
});
</script>


<style lang="less" scoped>
.gift-control {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 108px;
  background: #fff;
  border-radius: 0 0 8px 8px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 12px;

  .gift-list {
    display: flex;
    margin-right: 12px;
  }

  .gift-expand {
    width: 22px;
    height: 80px;
    position: relative;
    z-index: 2;
    margin-right: 12px;
    background: rgba(153, 153, 153, 0.05);
    border-radius: 4px;
    display: flex;
    align-items: center;
    cursor: pointer;

    &:hover {
      background: rgba(153, 153, 153, 0.1);
    }

    .expand-icon {
      height: 22px;
      width: 22px;
      background: url('@/assets/arrow-black.svg') no-repeat center center;
      background-size: 100%;
      transform: rotateZ(180deg);
      transition: all 0.3s;

      &.expanded {
        transform: rotateZ(0deg);
      }
    }
  }

  .stars {
    width: 92px;
    height: 80px;
    display: flex;
    align-items: center;
    flex-direction: column;

    :deep(.tooltip-star-desc) {
      width: 400px;
    }

    .title {
      font-size: 12px;
      color: #999;
      letter-spacing: 0;
      line-height: 18px;
      font-weight: 400;
      padding-left: 22px;
      background: url('@/assets/star-big.png') no-repeat left center;
      background-size: 18px;
    }

    .balance {
      font-size: 14px;
      color: #222;
      letter-spacing: 0;
      line-height: 18px;
      font-weight: 400;
      margin-top: 6px;
      text-align: center;
    }

    .top-up-btn {
      cursor: pointer;
      background: #ffd42a;
      border-radius: 4px;
      width: 100%;
      height: 28px;
      line-height: 28px;
      font-size: 14px;
      color: #333;
      text-align: center;
      font-weight: 700;
      margin-top: 8px;

      &:hover {
        background: #ecbb39;
      }

      &:active {
        opacity: 0.8;
      }
    }
  }
}

.gift-panel-wrapper {
  position: absolute;
  z-index: 1;
  right: 8px;
  top: -324px;
  height: 316px;
  transform: translateY(0);
  transition: all 0.2s linear;
  overflow: hidden;

  &:hover {
    overflow: visible;
  }

  &.not-expanded {
    height: 0;
    transform: translateY(380px);
  }
}

.dialog-top_up {
  &__.header {
    font-size: 18px;
    color: #333;
    letter-spacing: 0;
    line-height: 22px;
    font-weight: 700;
    width: 412px;
  }

  &__body {
    .content {
      font-size: 14px;
      color: #999;
      line-height: 18px;
      font-weight: 400;
    }

    .btns {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 30px;

      .cancel-btn {
        font-size: 16px;
        color: #b1800f;
        letter-spacing: 0;
        line-height: 44px;
        font-weight: 700;
        background: #fff;
        border: 2px solid rgba(177, 128, 15, 1);
        border-radius: 4px;
        width: 211px;
        height: 44px;
        text-align: center;
        cursor: pointer;

        &:hover {
          background: rgba(255, 225, 131, 0.15);
        }
      }

      .buy-btn {
        font-size: 16px;
        color: #333;
        text-align: center;
        line-height: 44px;
        font-weight: 700;
        background: #ffd42a;
        border-radius: 4px;
        width: 211px;
        height: 44px;
        cursor: pointer;

        &:hover {
          background: #e4c44d;
        }
      }
    }
  }
}
</style>

<style lang="less">
.el-dialog.dialog-top_up {
  width: 510px;

  /* height: 262px; */

  background: #fff;
  border-radius: 12px;

  .el-dialog__header {
    padding: 38px 36px 18px;

    .el-dialog__headerbtn {
      width: 45px;
      height: 48px;

      .el-dialog__close {
        font-size: 20px;
        color: #4b4b4b;
      }
    }
  }

  .el-dialog__body {
    padding: 0 36px 40px;
  }
}
</style>
